import type { ReactNode, ReactElement } from 'react';

import React, { useState, useCallback } from 'react';

import { Marker, GoogleMap, Circle } from '@react-google-maps/api';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import LazyLoadScript from 'src/components/LazyLoadScript';

export interface GoogleMapMarkerProps {
  pos?: Record<string, any>;
  radius?: number;
  /**
   * Shade an area of this many meters around `pos` instead of dropping a pin,
   * for points that are only approximate.
   */
  areaRadiusMeters?: number | null;
  sx?: any;
  [key: string]: any;
}

// ----------------------------------------------------------------------

/**
 * If the given LatLng is invalid (one of the components is not a number) then
 * return null. This prevents errors from being raised inside of google's maps
 * implementation.
 *
 * @param {{ lat?: number, lng?: number } | null} pos
 * @returns {{ lat: number, lng: number } | null}
 */
function sanitizeCoordinates(pos) {
  if (pos?.lat && pos?.lng) {
    return pos;
  }

  return null;
}

function GoogleMapMarker({
  pos,
  radius,
  areaRadiusMeters,
  sx,
  ...other
}: GoogleMapMarkerProps) {
  const theme = useTheme();
  const [, setMap] = useState(null);

  const onUnmount = useCallback(
    // eslint-disable-next-line no-unused-vars
    function callback(_map) {
      setMap(null);
    },
    [setMap]
  );

  const cleanPos = sanitizeCoordinates(pos);

  const mapOptions = {
    zoom: areaRadiusMeters ? 13 : 12,
    minZoom: 2,
    maxZoom: 18,
    center: cleanPos,
    controlSize: 24,
    scaleControl: true,
    streetViewControl: false,
    zoomControl: true,
    mapTypeControl: false,
    fullscreenControl: true,
  };

  return (
    <Box sx={[{ width: '100%', height: '100%' }, sx]}>
      <LazyLoadScript>
        <GoogleMap
          onUnmount={onUnmount}
          options={mapOptions}
          mapContainerStyle={{
            width: '100%',
            height: '100%',
          }}
          {...other}
        >
          {cleanPos && areaRadiusMeters ? (
            <Circle
              center={cleanPos}
              radius={areaRadiusMeters}
              options={{
                clickable: false,
                fillColor: theme.palette.primary.main,
                fillOpacity: 0.15,
                strokeColor: theme.palette.primary.main,
                strokeOpacity: 0.6,
                strokeWeight: 1,
              }}
            />
          ) : null}
          {cleanPos && !areaRadiusMeters && (
            <>
              <Marker
                position={cleanPos}
                icon={{
                  path: 'M8 0C3.61277632-.00021937.04387223 3.53299568 0 7.92 0 13.4 7.05 19.5 7.35 19.76c.374224.3200877.925776.3200877 1.3 0C9 19.5 16 13.4 16 7.92 15.9561278 3.53299568 12.3872237-.00021937 8 0zm0 11c-1.9329966 0-3.5-1.5670034-3.5-3.5C4.5 5.56700338 6.0670034 4 8 4s3.5 1.56700338 3.5 3.5c0 .9282577-.3687489 1.8184964-1.0251263 2.4748737C9.8184964 10.6312511 8.9282577 11 8 11z',
                  fillColor: theme.palette.error.main,
                  fillOpacity: 1.0,
                  strokeWeight: 0,
                  scale: 1.5,
                }}
              />
              {radius && (
                <Circle
                  center={cleanPos}
                  radius={radius * 1609.34}
                  options={{
                    fillColor: 'transparent',
                    fillOpacity: 0,
                    strokeColor: theme.palette.primary.main,
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                  }}
                />
              )}
            </>
          )}
        </GoogleMap>
      </LazyLoadScript>
    </Box>
  );
}

export default GoogleMapMarker;
